import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import PersonnelSearch from '../components/PersonnelSearch';
import useFetch from 'use-http';
import DialogList from 'src/dialogs';
import actions from 'src/actions';
import PersonnelNavBar from '../components/PersonnelNavBar';
import PersonnelToast from '../components/PersonnelToast';
import TSDILandman from 'src/models/personnel/TSDILandman';
import { IRootState } from 'src/reducers';

import '../BenefitStyleSheet.scss';
import BenefitsHistory from 'src/models/personnel/EmployeeBenefitsHistory';
import BenefitsHistoryEntry from 'src/models/personnel/BenefitHistoryEntry';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import PersonnelAdvancedQuickFind from '../dialogs/PersonnelAdvancedQuickFindDialog';

enum TableViewTypes {
    Annual = 'Annual',
    Month = 'Month',
    Period = 'Pay Period',
    Day = 'Day'
}

class BenefitsHistoryCollection {
    annual: Array<BenefitsHistoryEntry>;
    month: Array<BenefitsHistoryEntry>;
    period: Array<BenefitsHistoryEntry>;
    day: Array<BenefitsHistoryEntry>;
}

const EmployeeBenefitsHistoryPage = () => {
    const { get, response } = useFetch();
    const [toast, setToast] = useState<{ message: string; color?: string, persistent?: boolean; } | null>(null);
    const selectedPersonnel = useSelector((state: IRootState) => state.personnel.selectedPersonnel);
    const [allBenefitsHistory, setAllBenefitsHistory] = useState<Array<BenefitsHistory>>();
    const [filteredHistory, setFilteredHistory] = useState<Array<BenefitsHistoryEntry>>();
    const [selectedView, setSelectedView] = useState<TableViewTypes>(TableViewTypes.Annual);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const dispatch = useDispatch();

    // Initial render
    useEffect(() => {
        onCreate();

        return () => {
            onDestroy();
        };
    }, []);

    useEffect(() => {
        initializeData();
    }, [selectedPersonnel]);

    // simplifying the rows when a new filter is selected or data loads
    useEffect(() => {
        simplifyRows();
    }, [allBenefitsHistory, selectedView]);

    const onCreate = () => {
        console.log('EmployeeBenefitsHistoryPage onCreate()');
        initializeData();
    };

    const onDestroy = () => {
        console.log('EmployeeBenefitsHistoryPage onDestroy()');
    };

    const showToast = (message: string, color?: string, persistent?: boolean) => {
        setToast({ message, persistent, color });
    };

    const displayTitle = () => {
        const title = selectedPersonnel ? `Benefits History - ${selectedPersonnel?.akaName ? selectedPersonnel?.akaName : selectedPersonnel?.fName + ' ' + selectedPersonnel?.lName}` : 'Benefits History';
        return title;
    };

    // gets all data per personnel selected
    const initializeData = async () => {
        if (!!selectedPersonnel) {
            setIsLoading(true);
            setAllBenefitsHistory(null);
            setSelectedView(TableViewTypes.Annual);
            getBenefitsHistory();
        }
    };

    const getBenefitsHistory = async () => {
        setIsLoading(true);
        let results = await get(`/v1.0/landmen/benefits/${selectedPersonnel.tsdNo}/get-benefits-history`);

        if (response.ok && results) {
            setAllBenefitsHistory(results);
            setIsLoading(false);
        } else {
            showToast('Unable to fetch benefit history data', `rgba(255,0,0,0.5)`);
        }
    };

    /**
     * Sets the selected Personnel to the store if the given ID is valid and it's not already the same personnel
     * @param personnel - personnel payload
     */
    const setSelectedPersonnel = (personnel: TSDILandman) => {
        if (personnel) {
            personnel?.lmNo !== selectedPersonnel?.lmNo
                ? dispatch({ type: '[personnel] SET_SELECTED_PERSONNEL', payload: personnel })
                : showToast('Selected personnel already set', `rgba(78,117,154,0.5)`);

        } else {
            showToast('Selected personnel is undefined', `rgba(255,0,0,0.5)`);
        }
    };

    // start dialog
    const toggleAdvancedQuickFind = () => {
        dispatch(actions.layout.showDialog(DialogList.PERSONNEL_ADVANCED_QUICK_FIND));
    };

    const hideDialog = () => {
        dispatch(actions.layout.hideDialog());
    };
    // end dialog

    // simplifying rows
    const simplifyRows = () => {
        let simplifiedRows: BenefitsHistoryCollection = new BenefitsHistoryCollection();
        simplifiedRows.annual = new Array<BenefitsHistoryEntry>();
        simplifiedRows.month = new Array<BenefitsHistoryEntry>();
        simplifiedRows.period = new Array<BenefitsHistoryEntry>();
        simplifiedRows.day = new Array<BenefitsHistoryEntry>();

        allBenefitsHistory?.forEach((entry) => {
            let annualRow: BenefitsHistoryEntry = new BenefitsHistoryEntry();
            annualRow.tsdNo = entry?.tsdNo;
            annualRow.akaName = entry?.akaName;
            annualRow.year = entry?.year;

            // annual values
            annualRow.health = entry?.annualHealth;
            annualRow.employeeHealth = entry?.annualEmployeeHealth;
            annualRow.companyHealth = entry?.annualCompanyHealth;

            annualRow.dental = entry?.annualDental;
            annualRow.employeeDental = entry?.annualEmployeeDental;
            annualRow.companyDental = entry?.annualCompanyDental;

            annualRow.vision = entry?.annualVision;
            annualRow.employeeVision = entry?.annualEmployeeVision;
            annualRow.companyVision = entry?.annualCompanyVision;

            annualRow.life = entry?.annualLife;
            annualRow.employeeLife = entry?.annualEmployeeLife;
            annualRow.companyLife = entry?.annualCompanyLife;

            annualRow.disability = entry?.annualDisability;
            annualRow.employeeDisability = entry?.annualEmployeeDisability;
            annualRow.companyDisability = entry?.annualCompanyDisability;

            annualRow.vacation = entry?.annualVacation;
            annualRow.employeeVacation = entry?.annualEmployeeVacation;
            annualRow.companyVacation = entry?.annualCompanyVacation;

            annualRow.personal = entry?.annualPersonal;
            annualRow.employeePersonal = entry?.annualEmployeePersonal;
            annualRow.companyPersonal = entry?.annualCompanyPersonal;

            annualRow.holiday = entry?.annualHoliday;
            annualRow.employeeHoliday = entry?.annualEmployeeHoliday;
            annualRow.companyHoliday = entry?.annualCompanyHoliday;

            annualRow._401K = entry?.annual401K;
            annualRow.employee401K = entry?.annualEmployee401K;
            annualRow.company401K = entry?.annualCompany401K;

            annualRow.fica = entry?.annualFICA;
            annualRow.employeeFICA = entry?.annualEmployeeFICA;
            annualRow.companyFICA = entry?.annualCompanyFICA;

            annualRow.workersComp = entry?.annualWorkersComp;
            annualRow.employeeWorkersComp = entry?.annualEmployeeWorkersComp;
            annualRow.companyWorkersComp = entry?.annualCompanyWorkersComp;

            annualRow.insurance = entry?.annualInsurance;
            annualRow.employeeInsurance = entry?.annualEmployeeInsurance;
            annualRow.companyInsurance = entry?.annualCompanyInsurance;

            annualRow.pto = entry?.annualPto;
            annualRow.employeePto = entry?.annualEmployeePto;
            annualRow.companyPto = entry?.annualCompanyPto;

            annualRow.benefits = entry?.annualBenefits;
            annualRow.employeeBenefits = entry?.annualBenefits;
            annualRow.companyBenefits = entry?.annualBenefits;

            annualRow.excludingPto = entry?.annualExcludingPto;
            annualRow.employeeExcludingPto = entry?.annualEmployeeExcludingPto;
            annualRow.companyExcludingPto = entry?.annualCompanyExcludingPto;

            annualRow.salary = entry?.annualSalary;

            annualRow.payBenefits = entry?.annualPayBenefits;
            annualRow.employeePayBenefits = entry?.annualEmployeePayBenefits;
            annualRow.companyPayBenefits = entry?.annualCompanyPayBenefits;

            annualRow.active = entry?.active;

            simplifiedRows.annual = simplifiedRows.annual.concat(annualRow);

            // month values
            let monthRow: BenefitsHistoryEntry = new BenefitsHistoryEntry();
            monthRow.tsdNo = entry?.tsdNo;
            monthRow.akaName = entry?.akaName;
            monthRow.year = entry?.year;

            monthRow.health = entry?.monthHealth;
            monthRow.employeeHealth = entry?.monthEmployeeHealth;
            monthRow.companyHealth = entry?.monthCompanyHealth;

            monthRow.dental = entry?.monthDental;
            monthRow.employeeDental = entry?.monthEmployeeDental;
            monthRow.companyDental = entry?.monthCompanyDental;

            monthRow.vision = entry?.monthVision;
            monthRow.employeeVision = entry?.monthEmployeeVision;
            monthRow.companyVision = entry?.monthCompanyVision;

            monthRow.life = entry?.monthLife;
            monthRow.employeeLife = entry?.monthEmployeeLife;
            monthRow.companyLife = entry?.monthCompanyLife;

            monthRow.disability = entry?.monthDisability;
            monthRow.employeeDisability = entry?.monthEmployeeDisability;
            monthRow.companyDisability = entry?.monthCompanyDisability;

            monthRow.vacation = entry?.monthVacation;
            monthRow.employeeVacation = entry?.monthEmployeeVacation;
            monthRow.companyVacation = entry?.monthCompanyVacation;

            monthRow.personal = entry?.monthPersonal;
            monthRow.employeePersonal = entry?.monthEmployeePersonal;
            monthRow.companyPersonal = entry?.monthCompanyPersonal;

            monthRow.holiday = entry?.monthHoliday;
            monthRow.employeeHoliday = entry?.monthEmployeeHoliday;
            monthRow.companyHoliday = entry?.monthCompanyHoliday;

            monthRow._401K = entry?.month401K;
            monthRow.employee401K = entry?.monthEmployee401K;
            monthRow.company401K = entry?.monthCompany401K;

            monthRow.fica = entry?.monthFICA;
            monthRow.employeeFICA = entry?.monthEmployeeFICA;
            monthRow.companyFICA = entry?.monthCompanyFICA;

            monthRow.workersComp = entry?.monthWorkersComp;
            monthRow.employeeWorkersComp = entry?.monthEmployeeWorkersComp;
            monthRow.companyWorkersComp = entry?.monthCompanyWorkersComp;

            monthRow.insurance = entry?.monthInsurance;
            monthRow.employeeInsurance = entry?.monthEmployeeInsurance;
            monthRow.companyInsurance = entry?.monthCompanyInsurance;

            monthRow.pto = entry?.monthPto;
            monthRow.employeePto = entry?.monthEmployeePto;
            monthRow.companyPto = entry?.monthCompanyPto;

            monthRow.benefits = entry?.monthBenefits;
            monthRow.employeeBenefits = entry?.monthBenefits;
            monthRow.companyBenefits = entry?.monthBenefits;

            monthRow.excludingPto = entry?.monthExcludingPto;
            monthRow.employeeExcludingPto = entry?.monthEmployeeExcludingPto;
            monthRow.companyExcludingPto = entry?.monthCompanyExcludingPto;

            monthRow.salary = entry?.monthSalary;

            monthRow.payBenefits = entry?.monthPayBenefits;
            monthRow.employeePayBenefits = entry?.monthEmployeePayBenefits;
            monthRow.companyPayBenefits = entry?.monthCompanyPayBenefits;

            monthRow.active = entry?.active;

            simplifiedRows.month = simplifiedRows.month.concat(monthRow);

            // pay period values
            let periodRow: BenefitsHistoryEntry = new BenefitsHistoryEntry();
            periodRow.tsdNo = entry?.tsdNo;
            periodRow.akaName = entry?.akaName;
            periodRow.year = entry?.year;

            periodRow.health = entry?.periodHealth;
            periodRow.employeeHealth = entry?.periodEmployeeHealth;
            periodRow.companyHealth = entry?.periodCompanyHealth;

            periodRow.dental = entry?.periodDental;
            periodRow.employeeDental = entry?.periodEmployeeDental;
            periodRow.companyDental = entry?.periodCompanyDental;

            periodRow.vision = entry?.periodVision;
            periodRow.employeeVision = entry?.periodEmployeeVision;
            periodRow.companyVision = entry?.periodCompanyVision;

            periodRow.life = entry?.periodLife;
            periodRow.employeeLife = entry?.periodEmployeeLife;
            periodRow.companyLife = entry?.periodCompanyLife;

            periodRow.disability = entry?.periodDisability;
            periodRow.employeeDisability = entry?.periodEmployeeDisability;
            periodRow.companyDisability = entry?.periodCompanyDisability;

            periodRow.vacation = entry?.periodVacation;
            periodRow.employeeVacation = entry?.periodEmployeeVacation;
            periodRow.companyVacation = entry?.periodCompanyVacation;

            periodRow.personal = entry?.periodPersonal;
            periodRow.employeePersonal = entry?.periodEmployeePersonal;
            periodRow.companyPersonal = entry?.periodCompanyPersonal;

            periodRow.holiday = entry?.periodHoliday;
            periodRow.employeeHoliday = entry?.periodEmployeeHoliday;
            periodRow.companyHoliday = entry?.periodCompanyHoliday;

            periodRow._401K = entry?.period401K;
            periodRow.employee401K = entry?.periodEmployee401K;
            periodRow.company401K = entry?.periodCompany401K;

            periodRow.fica = entry?.periodFICA;
            periodRow.employeeFICA = entry?.periodEmployeeFICA;
            periodRow.companyFICA = entry?.periodCompanyFICA;

            periodRow.workersComp = entry?.periodWorkersComp;
            periodRow.employeeWorkersComp = entry?.periodEmployeeWorkersComp;
            periodRow.companyWorkersComp = entry?.periodCompanyWorkersComp;

            periodRow.insurance = entry?.periodInsurance;
            periodRow.employeeInsurance = entry?.periodEmployeeInsurance;
            periodRow.companyInsurance = entry?.periodCompanyInsurance;

            periodRow.pto = entry?.periodPto;
            periodRow.employeePto = entry?.periodEmployeePto;
            periodRow.companyPto = entry?.periodCompanyPto;

            periodRow.benefits = entry?.periodBenefits;
            periodRow.employeeBenefits = entry?.periodBenefits;
            periodRow.companyBenefits = entry?.periodBenefits;

            periodRow.excludingPto = entry?.periodExcludingPto;
            periodRow.employeeExcludingPto = entry?.periodEmployeeExcludingPto;
            periodRow.companyExcludingPto = entry?.periodCompanyExcludingPto;

            periodRow.salary = entry?.periodSalary;

            periodRow.payBenefits = entry?.periodPayBenefits;
            periodRow.employeePayBenefits = entry?.periodEmployeePayBenefits;
            periodRow.companyPayBenefits = entry?.periodCompanyPayBenefits;

            periodRow.active = entry?.active;

            simplifiedRows.period = simplifiedRows.period.concat(periodRow);

            // day values
            let dayRow: BenefitsHistoryEntry = new BenefitsHistoryEntry();
            dayRow.tsdNo = entry?.tsdNo;
            dayRow.akaName = entry?.akaName;
            dayRow.year = entry?.year;

            dayRow.health = entry?.dayHealth;
            dayRow.employeeHealth = entry?.dayEmployeeHealth;
            dayRow.companyHealth = entry?.dayCompanyHealth;

            dayRow.dental = entry?.dayDental;
            dayRow.employeeDental = entry?.dayEmployeeDental;
            dayRow.companyDental = entry?.dayCompanyDental;

            dayRow.vision = entry?.dayVision;
            dayRow.employeeVision = entry?.dayEmployeeVision;
            dayRow.companyVision = entry?.dayCompanyVision;

            dayRow.life = entry?.dayLife;
            dayRow.employeeLife = entry?.dayEmployeeLife;
            dayRow.companyLife = entry?.dayCompanyLife;

            dayRow.disability = entry?.dayDisability;
            dayRow.employeeDisability = entry?.dayEmployeeDisability;
            dayRow.companyDisability = entry?.dayCompanyDisability;

            dayRow.vacation = entry?.dayVacation;
            dayRow.employeeVacation = entry?.dayEmployeeVacation;
            dayRow.companyVacation = entry?.dayCompanyVacation;

            dayRow.personal = entry?.dayPersonal;
            dayRow.employeePersonal = entry?.dayEmployeePersonal;
            dayRow.companyPersonal = entry?.dayCompanyPersonal;

            dayRow.holiday = entry?.dayHoliday;
            dayRow.employeeHoliday = entry?.dayEmployeeHoliday;
            dayRow.companyHoliday = entry?.dayCompanyHoliday;

            dayRow._401K = entry?.day401K;
            dayRow.employee401K = entry?.dayEmployee401K;
            dayRow.company401K = entry?.dayCompany401K;

            dayRow.fica = entry?.dayFICA;
            dayRow.employeeFICA = entry?.dayEmployeeFICA;
            dayRow.companyFICA = entry?.dayCompanyFICA;

            dayRow.workersComp = entry?.dayWorkersComp;
            dayRow.employeeWorkersComp = entry?.dayEmployeeWorkersComp;
            dayRow.companyWorkersComp = entry?.dayCompanyWorkersComp;

            dayRow.insurance = entry?.dayInsurance;
            dayRow.employeeInsurance = entry?.dayEmployeeInsurance;
            dayRow.companyInsurance = entry?.dayCompanyInsurance;

            dayRow.pto = entry?.dayPto;
            dayRow.employeePto = entry?.dayEmployeePto;
            dayRow.companyPto = entry?.dayCompanyPto;

            dayRow.benefits = entry?.dayBenefits;
            dayRow.employeeBenefits = entry?.dayBenefits;
            dayRow.companyBenefits = entry?.dayBenefits;

            dayRow.excludingPto = entry?.dayExcludingPto;
            dayRow.employeeExcludingPto = entry?.dayEmployeeExcludingPto;
            dayRow.companyExcludingPto = entry?.dayCompanyExcludingPto;

            dayRow.salary = entry?.daySalary;

            dayRow.payBenefits = entry?.dayPayBenefits;
            dayRow.employeePayBenefits = entry?.dayEmployeePayBenefits;
            dayRow.companyPayBenefits = entry?.dayCompanyPayBenefits;

            dayRow.active = entry?.active;

            simplifiedRows.day = simplifiedRows.day.concat(dayRow);
        });

        // setIsLoading(false);
        updateTableView(selectedView, simplifiedRows);
    };

    // filter
    const updateTableView = (view: TableViewTypes, data: BenefitsHistoryCollection) => {
        switch (view) {
            case TableViewTypes.Annual:
                setFilteredHistory(data?.annual);
                break;
            case TableViewTypes.Month:
                setFilteredHistory(data?.month);
                break;
            case TableViewTypes.Period:
                setFilteredHistory(data?.period);
                break;
            case TableViewTypes.Day:
                setFilteredHistory(data?.day);
                break;
            default:
                setFilteredHistory(null);
                break;
        }
    };

    const updateViewType = () => {
        let e = (document.getElementById('viewSelection')) as HTMLSelectElement;
        let option = e.options[e.selectedIndex];

        let view = option.value as TableViewTypes;
        setSelectedView(view);
    };

    return (
        <div>
            <PersonnelNavBar />
            <div>
                {toast && (
                    <PersonnelToast
                        message={toast.message}
                        color={toast.color}
                        onClose={() => setToast(null)}
                        persistent={toast.persistent}
                    />
                )}
            </div>
            <PersonnelSearch toggleAdvancedQuickFind={toggleAdvancedQuickFind} />
            <Page>
                <Page.Section width={14}>
                    <Panel backgroundColor="#D3D3D3">
                        <Panel panelTitle={displayTitle()}>
                            {isLoading && (
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        height: '50vh',
                                        width: '100%',
                                    }}
                                >
                                    <LoadingSpinner />
                                </div>
                            )}
                            {filteredHistory?.length > 0 &&
                                <div style={{ overflowY: 'scroll', maxHeight: '35rem' }}>
                                    <table style={{ width: '100%' }}>
                                        <thead>
                                            <tr style={{ background: '#d6d6d6' }}>
                                                <th style={{ width: '4%' }} className="sticky-header">Year</th>
                                                <th style={{ width: '6%' }} className="sticky-header">Health</th>
                                                <th style={{ width: '6%' }} className="sticky-header">Vision</th>
                                                <th style={{ width: '6%' }} className="sticky-header">Dental</th>
                                                <th style={{ width: '6%' }} className="sticky-header">Life</th>
                                                <th style={{ width: '6%' }} className="sticky-header">Disability</th>
                                                <th style={{ width: '6%' }} className="sticky-header">Total Insurance</th>
                                                <th style={{ width: '6%' }} className="sticky-header">Vacation</th>
                                                <th style={{ width: '6%' }} className="sticky-header">Personal</th>
                                                <th style={{ width: '6%' }} className="sticky-header">Holiday</th>
                                                <th style={{ width: '6%' }} className="sticky-header">401K</th>
                                                <th style={{ width: '6%' }} className="sticky-header">FICA</th>
                                                <th style={{ width: '6%' }} className="sticky-header">Workers Comp</th>
                                                <th style={{ width: '6%' }} className="sticky-header">Benefit Total</th>
                                                <th style={{ width: '6%' }} className="sticky-header">w/o PTO</th>
                                                <th style={{ width: '6%' }} className="sticky-header">Salary</th>
                                                <th style={{ width: '6%' }} className="sticky-header">Benefits + Salary</th>
                                            </tr>
                                        </thead>
                                        {filteredHistory?.map((history: BenefitsHistoryEntry, index: number) => {
                                            return (
                                                <tbody key="benefits-history-table">
                                                    <tr>
                                                        <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                            {history?.year}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.health)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.vision)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.dental)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.life)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.disability)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.insurance)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.vacation)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.personal)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.holiday)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?._401K)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.fica)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.workersComp)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.benefits)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.excludingPto)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.salary)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.salary + history?.benefits)}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            );
                                        })}
                                    </table>
                                </div>}
                            {!selectedPersonnel && <h2 style={{ textAlign: 'center' }}>No personnel selected</h2>}
                            {((filteredHistory?.length <= 0 || !filteredHistory) && !isLoading) && <h3 style={{ textAlign: 'center' }}>No history found</h3>}
                            <hr />
                            <br />
                            <div className="form-row">
                                <label style={{ paddingRight: '1rem' }}>View Period</label>
                                <select name="viewSelection" id="viewSelection" onChange={() => updateViewType()}>
                                    <option value={TableViewTypes.Annual}>Annual</option>
                                    <option value={TableViewTypes.Month}>Monthly</option>
                                    <option value={TableViewTypes.Period}>Pay Period</option>
                                    <option value={TableViewTypes.Day}>Day</option>
                                </select>
                            </div>
                        </Panel>
                    </Panel>
                </Page.Section>
            </Page>
            <PersonnelAdvancedQuickFind
                dialogName={DialogList.PERSONNEL_ADVANCED_QUICK_FIND}
                hideDialog={hideDialog}
                selectedPersonnel={selectedPersonnel}
                selectPersonnel={setSelectedPersonnel}
            />
        </div>
    );
};

export default EmployeeBenefitsHistoryPage;


// WEBPACK FOOTER //
// ./src/components/personnel/pages/EmployeeBenefitsHistoryPage.tsx